import { MESSAGE_TAG } from "@/schemas";
import type { MessageDiscussion } from "@/types";
import { AxiosError } from "axios";
import get from "lodash/get";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import omitBy from "lodash/omitBy";
import { generateUUID } from "pubnub";
import type { Paths } from "type-fest";
import env from "../env.config";
import cookie from "./cookie";

export function isAuthenticating() {
	return (
		!!cookie.get(env.authKeyClient) &&
		!!cookie.get(env.authKeyUid) &&
		!!cookie.get(env.authKeyClient)
	);
}

export function isWebview() {
	if ("ReactNativeWebView" in window) return !!window.ReactNativeWebView;
	return cookie.get("WEBVIEW") === "true";
}

/**
 * @description Generate a random integer between a range
 * @example
 * // Returns a random integer between 1000 and 1000000
 * randInt(1000, 1000000); // 123456
 */
export function randInt(min = 1000, max = 1000000) {
	return Math.floor(Math.random() * (max - min + 1) + min);
}

export function capitalized(str: string) {
	if (!str) return str;
	let value = str as string;
	if (Array.isArray(str) && str.length) {
		value = str[0];
	}
	return value
		?.split(" ")
		?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		?.join(" ");
}

export function randUuid(length = 4): string {
	return Math.random()
		.toString(36)
		.substring(2, 2 + length);
}

export function randFileName(ext: string, length = 4): string {
	return `${randUuid(length)}-${Date.now()}${ext}`;
}

export function mockArrUuid(length = 4): string[] {
	return Array.from({ length }, () => randUuid());
}

export function isFiles(files: File | FileList): files is FileList {
	return (
		files instanceof FileList &&
		Array.from(files).length > 0 &&
		Array.from(files).every((val) => isFile(val))
	);
}

export function isFile(file: unknown): file is File {
	return file instanceof File;
}

export function fileToString(file: FileList): string[];
export function fileToString(file: File): string[];
export function fileToString(file: File | FileList): string[] {
	if (isFile(file)) return [URL.createObjectURL(file)];
	return Array.from(file).map((f) => URL.createObjectURL(f));
}

export function getReverseIndex(length: number, index: number) {
	return length - index - 1;
}

export function cleanObject<T extends Record<string, any>>(obj: T): T {
	if (isEmpty(obj)) return obj;
	return omitBy(
		obj,
		(value) =>
			isNil(value) || value === "" || (isArray(value) && isEmpty(value)),
	) as T;
}

export function fileNameFromFieldKey(key: string | URL) {
	if (isString(key)) return key.split("/")?.pop() || "";
	return key.pathname.split("/")?.pop() || "";
}

export function isLink(str: string) {
	try {
		const url = new URL(str);
		return url.protocol === "http:" || url.protocol === "https:";
	} catch (error) {
		return false;
	}
}

export function intToHumanFileSize(size: number) {
	const i = Math.floor(Math.log(size) / Math.log(1024));
	return (
		// @ts-ignore
		// biome-ignore lint/style/useTemplate: <explanation>
		(size / 1024 ** i).toFixed(2) * 1 + " " + ["B", "kB", "MB", "GB", "TB"][i]
	);
}

/**
 * @example
 * isUrlImage(https://fastly.picsum.photos/id/237/200/300.jpg?hmac=TmmQSbShHz9CdQm0NkEjx1Dyh_Y984R9LpNrpvH2D_U); // true
 */
export function isUrlImage(url: string) {
	const imageExtensions = [
		"jpg",
		"jpeg",
		"png",
		"gif",
		"bmp",
		"webp",
		"svg",
		"tiff",
	];

	try {
		const urlObj = new URL(url);
		const extension = urlObj.pathname.split(".").pop()?.toLowerCase();
		if (!extension) return false;
		return imageExtensions.includes(extension);
	} catch (e) {
		// Invalid URL
		return false;
	}
}

/**
 * @example
 * genChannelName("single", "John Doe"); // "single.123456"
 */
export const genChannelName = (prefix: "single" | "group") => {
	const unix = Date.now();
	return `${prefix}.${unix}`;
};

export const isTrue = (condition: boolean | string) => {
	return typeof condition === "string" ? condition === "true" : condition;
};

export const isBiggerThanZero = (count: string | number) => +count > 0;

export const getMessageTagLabel = (
	message: MessageDiscussion,
	chatModule: "cases" | "chats" | null = "chats",
): string | string[] | null => {
	if (message.tags === MESSAGE_TAG.AI) {
		return "HEMI AI";
	}
	if (chatModule === "chats") {
		if (message.document_count) return "media";
	}
	if (chatModule === "cases") {
		let tags = message.tags as string | string[];
		if (message.document_count) {
			const msgTags = message?.documents?.[0]?.tags?.split("|");
			if (msgTags.length === 1) {
				tags = msgTags[0];
			} else {
				tags = msgTags;
			}
		}
		if (tags && !Array.isArray(tags)) {
			if (tags.toLowerCase() === "results") return "result";
			if (tags.includes("advice")) return "advice";
		}
		return tags || null;
	}
	return message.tags || null;
};

export const isObject = (value: unknown): value is Record<string, any> => {
	return value !== null && typeof value === "object" && !Array.isArray(value);
};

/**
 * @format hemi-#{SecureRandom.uuid}-#{Time.zone.now.to_i}"
 */
export const genHemiUid = (): string => {
	const uuid = generateUUID();
	const now = Date.now();
	return `hemi-${uuid}-${now}`;
};

export const genCaseTitle = (
	caseTitle: string,
	patientName?: string,
	patientSurename?: string,
) => {
	return patientName || patientSurename
		? [patientName, patientSurename].join(" ")
		: caseTitle;
};

export function getProgressPercentage(current: number, total: number): number {
	if (total <= 0) {
		throw new Error("Total value must be greater than zero.");
	}
	const percentage = (current / total) * 100;
	return Math.min(Math.max(percentage, 0), 100);
}

export function isAxiosError<T = unknown, D = unknown>(
	error: unknown,
): error is AxiosError<T, D>;
export function isAxiosError<T = unknown, D = unknown>(
	error: unknown,
	status: number,
): error is AxiosError<T, D>;
export function isAxiosError<T = unknown, D = unknown>(
	error: unknown,
	status?: number,
): error is AxiosError<T, D> {
	if (!isObject(error)) return false;
	if (!(error instanceof AxiosError)) return false;
	if (status && error.response?.status !== status) return false;
	return true;
}

export const isWebView = () =>
	!!localStorage.getItem(env.webviewKey) &&
	localStorage.getItem(env.webviewKey) === "true";

export const isMpisAccount = () =>
	!!cookie.get("account-type") && cookie.get("account-type") === "mpis";

export const isSsoAccount = () =>
	!!cookie.get("account-type") && cookie.get("account-type") === "sso";

export const pluralize = ({
	count,
	singularText,
	pluralText = "s",
	isIncludeNumber = true,
}: {
	count: number;
	singularText: string;
	pluralText?: string;
	isIncludeNumber?: boolean;
}) => {
	const pluralvalidation = isEmpty(pluralText) ? "s" : pluralText;
	const isEliminateNumber = isIncludeNumber ? `${count} ` : "";

	if (count === 1) {
		return `${isEliminateNumber.concat(singularText)}`;
	}
	if (pluralvalidation !== "s") {
		return `${isEliminateNumber.concat(pluralvalidation)}`;
	}

	return `${isEliminateNumber.concat(singularText + pluralvalidation)}`;
};

export function isItemLastArr<T>(
	arr: T[],
	item: T,
	predicate: Paths<T>,
): boolean {
	return (
		arr.findIndex(
			(f) => get(f, predicate as string) === get(item, predicate as string),
		) ===
		arr.length - 1
	);
}
